import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "../utils";
import { extractDominantColor } from "../../../utils/colorExtractor";

const EMPTY_TEAM = { name: "", abbreviation: "", mascot: "", color: "#FFFFFF", logo_url: "" };
const DEFAULT_FORM = {
  title: "", sport: "Football", channel_id: "", scheduled_date: "", scheduled_time: "", description: "",
  location: "", location_image: "", ticket_link: "",
  date_tbd: false, time_tbd: false,
  home_team: { ...EMPTY_TEAM, color: "#1a1a1a" },
  away_team: { ...EMPTY_TEAM, color: "#003087" },
  external_links: [], stats_link: "", scorebird_link: "", stats_provider: "prestosports", show_stats_overlay: true,
  event_id: "", auto_go_live: false, youtube_url: "", vcloud_url: "", m3u8_url: "",
  active_video_source: "auto", thumbnail_url: "",
  custom_url: "", home_maxpreps_link: "", away_maxpreps_link: ""
};

export function useBroadcasts(onRefresh) {
  const [broadcasts, setBroadcasts] = useState([]);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPastGameForm, setShowPastGameForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const thumbnailInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState({});
  const [newLink, setNewLink] = useState({ label: "", url: "", type: "stats" });
  const [expandedBroadcast, setExpandedBroadcast] = useState(null);
  const [vodForm, setVodForm] = useState({ youtube_vod_url: "", recording_url: "", home_score: "", away_score: "" });
  const [savingVod, setSavingVod] = useState(false);
  const [syncingScores, setSyncingScores] = useState({});
  const [extractingColor, setExtractingColor] = useState({ home: false, away: false });
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [archivingBroadcast, setArchivingBroadcast] = useState({});
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [savingPastGame, setSavingPastGame] = useState(false);
  const [pastGameData, setPastGameData] = useState({
    sport: "Football", game_date: "",
    home_team: { ...EMPTY_TEAM }, away_team: { ...EMPTY_TEAM },
    home_score: "", away_score: "", box_score_url: "", watch_url: "", event_id: ""
  });

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");

  const fetchBroadcasts = useCallback(async () => {
    try { const r = await axios.get(`${API}/broadcasts/schedule?include_dummy=true`, { headers: getAuthHeader() }); setBroadcasts(r.data); }
    catch (e) { console.error("Error:", e); }
  }, []);

  const fetchEvents = useCallback(async () => {
    try { const r = await axios.get(`${API}/events?include_unpublished=true`); setEvents(r.data || []); }
    catch (e) { console.error("Error fetching events:", e); }
  }, []);

  const fetchTeams = useCallback(async () => {
    try { const r = await axios.get(`${API}/teams`); setTeams(r.data || []); }
    catch (e) { console.error("Error fetching teams:", e); }
  }, []);

  useEffect(() => { fetchBroadcasts(); fetchEvents(); fetchTeams(); }, [fetchBroadcasts, fetchEvents, fetchTeams]);

  const handleExtractTeamColor = async (isHome) => {
    const logoUrl = isHome ? formData.home_team.logo_url : formData.away_team.logo_url;
    if (!logoUrl) return;
    setExtractingColor(prev => ({ ...prev, [isHome ? 'home' : 'away']: true }));
    try {
      const color = await extractDominantColor(logoUrl);
      setFormData(prev => ({ ...prev, [isHome ? 'home_team' : 'away_team']: { ...prev[isHome ? 'home_team' : 'away_team'], color } }));
    } catch (e) { console.error("Failed to extract color:", e); }
    finally { setExtractingColor(prev => ({ ...prev, [isHome ? 'home' : 'away']: false })); }
  };

  const handleGenerateThumbnail = async () => {
    if (!formData.home_team.logo_url || !formData.away_team.logo_url) { alert("Please add team logos for both Home and Away teams first."); return; }
    setGeneratingThumbnail(true);
    try {
      // Convert logos to base64 in the browser (bypasses server-side CORS/403)
      const toBase64 = (url) => new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext("2d").drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });

      const [homeB64, awayB64] = await Promise.all([
        toBase64(formData.home_team.logo_url),
        toBase64(formData.away_team.logo_url),
      ]);

      const res = await axios.post(`${API}/thumbnails/generate`, {
        home_team_color: formData.home_team.color || "#000000",
        away_team_color: formData.away_team.color || "#0066CC",
        home_team_logo_url: formData.home_team.logo_url,
        away_team_logo_url: formData.away_team.logo_url,
        home_team_logo_b64: homeB64 || undefined,
        away_team_logo_b64: awayB64 || undefined,
        width: 1920, height: 1080
      }, { headers: getAuthHeader() });

      if (res.data.success && res.data.url) {
        setFormData(prev => ({ ...prev, thumbnail_url: res.data.url }));
        if (res.data.warnings?.length) {
          alert(`Thumbnail generated but with issues:\n${res.data.warnings.join('\n')}\n\nTip: Make sure logo URLs are publicly accessible.`);
        }
      }
      else if (res.data.preview) setFormData(prev => ({ ...prev, thumbnail_url: res.data.preview }));
    } catch (e) { console.error("Error generating thumbnail:", e); alert("Failed to generate thumbnail."); }
    finally { setGeneratingThumbnail(false); }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumbnail(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/uploads/thumbnail`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) setFormData(prev => ({ ...prev, thumbnail_url: res.data.url }));
    } catch (e) { console.error("Error uploading thumbnail:", e); }
    finally { setUploadingThumbnail(false); if (thumbnailInputRef.current) thumbnailInputRef.current.value = ''; }
  };

  const selectTeam = (teamId, isHome) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    const td = { name: team.name, abbreviation: team.abbreviation || "", mascot: team.mascot || "", color: team.primary_color || team.color || "#FFFFFF", logo_url: team.logo_url || "" };
    setFormData(prev => ({ ...prev, [isHome ? 'home_team' : 'away_team']: td }));
  };

  const setTeamTBD = (isHome) => {
    const tbd = { name: "TBD", abbreviation: "TBD", mascot: "", color: "#666666", logo_url: "" };
    setFormData(prev => ({ ...prev, [isHome ? 'home_team' : 'away_team']: tbd }));
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editingId) await axios.put(`${API}/broadcasts/schedule/${editingId}`, formData, { headers: getAuthHeader() });
      else await axios.post(`${API}/broadcasts/schedule`, formData, { headers: getAuthHeader() });
      setShowForm(false); setEditingId(null); setFormData({ ...DEFAULT_FORM });
      fetchBroadcasts(); onRefresh();
    } catch (e) { console.error("Error:", e); }
    finally { setSaving(false); }
  };

  const handleEdit = (broadcast) => {
    setEditingId(broadcast.id);
    setFormData({
      title: broadcast.title || "", sport: broadcast.sport || "Football",
      channel_id: broadcast.channel_id || "", scheduled_date: broadcast.scheduled_date || "",
      scheduled_time: broadcast.scheduled_time || "", description: broadcast.description || "",
      date_tbd: broadcast.date_tbd || false, time_tbd: broadcast.time_tbd || false,
      home_team: broadcast.home_team || { ...EMPTY_TEAM }, away_team: broadcast.away_team || { ...EMPTY_TEAM },
      external_links: broadcast.external_links || [], stats_link: broadcast.stats_link || "",
      scorebird_link: broadcast.scorebird_link || "",
      stats_provider: broadcast.stats_provider || "prestosports", show_stats_overlay: broadcast.show_stats_overlay === true,
      event_id: broadcast.event_id || "",
      auto_go_live: broadcast.auto_go_live === true,
      youtube_url: broadcast.youtube_url || "", vcloud_url: broadcast.vcloud_url || "",
      m3u8_url: broadcast.m3u8_url || "", active_video_source: broadcast.active_video_source || "auto",
      thumbnail_url: broadcast.thumbnail_url || "",
      end_time: broadcast.end_time || "",
      location: broadcast.location || "",
      location_image: broadcast.location_image || "",
      ticket_link: broadcast.ticket_link || "",
      custom_url: broadcast.custom_url || "",
      home_maxpreps_link: broadcast.home_maxpreps_link || "",
      away_maxpreps_link: broadcast.away_maxpreps_link || ""
    });
    setShowForm(true);
    // Scroll to form after it renders
    setTimeout(() => {
      document.querySelector('[data-testid="broadcast-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleGoLive = async (id) => { await axios.post(`${API}/broadcasts/schedule/${id}/go-live`, {}, { headers: getAuthHeader() }); fetchBroadcasts(); onRefresh(); };
  const handleEndLive = async (id) => { await axios.post(`${API}/broadcasts/schedule/${id}/end-live`, {}, { headers: getAuthHeader() }); fetchBroadcasts(); onRefresh(); };
  const handleDelete = async (id) => { if (!window.confirm("Delete?")) return; await axios.delete(`${API}/broadcasts/schedule/${id}`, { headers: getAuthHeader() }); fetchBroadcasts(); onRefresh(); };
  const handleMarkComplete = async (id) => { if (!window.confirm("Mark this broadcast as completed?")) return; try { await axios.post(`${API}/broadcasts/schedule/${id}/complete`, {}, { headers: getAuthHeader() }); fetchBroadcasts(); } catch (e) { console.error("Error:", e); } };
  
  const handleToggleHidden = async (id, hidden) => {
    try { await axios.put(`${API}/broadcasts/schedule/${id}`, { is_hidden: hidden }, { headers: getAuthHeader() }); fetchBroadcasts(); }
    catch (e) { console.error("Error toggling visibility:", e); }
  };
  
  const handleSyncScores = async (id) => {
    setSyncingScores(prev => ({ ...prev, [id]: true }));
    try { const res = await axios.post(`${API}/broadcasts/schedule/${id}/sync-scores`, {}, { headers: getAuthHeader() }); if (res.data.success) { alert("Scores synced successfully!"); fetchBroadcasts(); } }
    catch (e) { alert(e.response?.data?.detail || "Failed to sync scores"); }
    finally { setSyncingScores(prev => ({ ...prev, [id]: false })); }
  };

  const handleSyncAllScores = async () => {
    try { const res = await axios.post(`${API}/broadcasts/statmoose/sync-all`, {}, { headers: getAuthHeader() }); alert(`Synced ${res.data.synced} games. Errors: ${res.data.errors}`); fetchBroadcasts(); }
    catch { alert("Failed to sync scores from StatMoose"); }
  };

  const handleArchiveToOnDemand = async (id) => {
    if (!window.confirm("Archive this broadcast to On-Demand?")) return;
    setArchivingBroadcast(prev => ({ ...prev, [id]: true }));
    try { const res = await axios.post(`${API}/broadcasts/schedule/${id}/archive-to-ondemand`, {}, { headers: getAuthHeader() }); if (res.data.success) { alert("Broadcast archived!"); fetchBroadcasts(); } }
    catch (e) { alert(e.response?.data?.message || "Failed to archive broadcast"); }
    finally { setArchivingBroadcast(prev => ({ ...prev, [id]: false })); }
  };

  const toggleExpanded = (id) => {
    if (expandedBroadcast === id) { setExpandedBroadcast(null); setVodForm({ youtube_vod_url: "", recording_url: "", home_score: "", away_score: "" }); }
    else {
      setExpandedBroadcast(id);
      const bc = broadcasts.find(b => b.id === id);
      setVodForm({ youtube_vod_url: bc?.youtube_vod_url || "", recording_url: bc?.recording_url || "", home_score: bc?.final_home_score != null ? String(bc.final_home_score) : "", away_score: bc?.final_away_score != null ? String(bc.final_away_score) : "" });
    }
  };

  const handleSaveVod = async (id) => {
    setSavingVod(true);
    try {
      if (vodForm.youtube_vod_url || vodForm.recording_url) await axios.post(`${API}/broadcasts/schedule/${id}/add-recording`, { youtube_vod_url: vodForm.youtube_vod_url, recording_url: vodForm.recording_url }, { headers: getAuthHeader() });
      if (vodForm.home_score !== "" || vodForm.away_score !== "") await axios.put(`${API}/broadcasts/schedule/${id}`, { final_home_score: vodForm.home_score !== "" ? parseInt(vodForm.home_score) : null, final_away_score: vodForm.away_score !== "" ? parseInt(vodForm.away_score) : null, is_completed: true }, { headers: getAuthHeader() });
      fetchBroadcasts(); setExpandedBroadcast(null); alert("VOD and scores saved successfully!");
    } catch { alert("Failed to save. Please try again."); }
    finally { setSavingVod(false); }
  };

  const handleSavePastGame = async () => {
    if (!pastGameData.home_team.name || !pastGameData.away_team.name || !pastGameData.game_date) { alert("Please fill in home team, away team, and date."); return; }
    setSavingPastGame(true);
    try {
      const title = `${pastGameData.away_team.name} @ ${pastGameData.home_team.name}`;
      await axios.post(`${API}/broadcasts/schedule`, {
        title, sport: pastGameData.sport, channel_id: "", scheduled_date: pastGameData.game_date, scheduled_time: "00:00", description: "",
        home_team: pastGameData.home_team, away_team: pastGameData.away_team,
        youtube_url: "", youtube_vod_url: pastGameData.watch_url || "", recording_url: pastGameData.watch_url || "",
        stats_link: pastGameData.box_score_url || "", stats_provider: "custom",
        external_links: pastGameData.box_score_url ? [{ label: "Box Score", url: pastGameData.box_score_url, type: "stats" }] : [],
        is_completed: true, final_home_score: parseInt(pastGameData.home_score) || 0, final_away_score: parseInt(pastGameData.away_score) || 0,
        event_id: pastGameData.event_id || null
      }, { headers: getAuthHeader() });
      setPastGameData({ sport: "Football", game_date: "", home_team: { ...EMPTY_TEAM }, away_team: { ...EMPTY_TEAM }, home_score: "", away_score: "", box_score_url: "", watch_url: "", event_id: "" });
      setShowPastGameForm(false); fetchBroadcasts();
    } catch { alert("Failed to save past game."); }
    finally { setSavingPastGame(false); }
  };

  // Bulk actions
  const toggleSelectAll = () => { setSelectedIds(selectedIds.size === broadcasts.length ? new Set() : new Set(broadcasts.map(b => b.id))); };
  const toggleSelect = (id) => { setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; }); };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || !window.confirm(`Delete ${selectedIds.size} selected broadcast(s)?`)) return;
    try { await axios.post(`${API}/broadcasts/schedule/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: getAuthHeader() }); setSelectedIds(new Set()); fetchBroadcasts(); onRefresh(); }
    catch { alert("Failed to delete broadcasts."); fetchBroadcasts(); }
  };
  const handleBulkEdit = async () => {
    if (selectedIds.size === 0 || !bulkEditField || bulkEditValue === "" || !window.confirm(`Update "${bulkEditField}" to "${bulkEditValue}" for ${selectedIds.size} broadcast(s)?`)) return;
    try { await Promise.all(Array.from(selectedIds).map(id => axios.put(`${API}/broadcasts/schedule/${id}`, { [bulkEditField]: bulkEditValue }, { headers: getAuthHeader() }))); setSelectedIds(new Set()); setBulkEditField(""); setBulkEditValue(""); fetchBroadcasts(); onRefresh(); }
    catch { alert("Some updates failed."); fetchBroadcasts(); }
  };

  const addLink = () => { if (newLink.label && newLink.url) { setFormData(prev => ({ ...prev, external_links: [...prev.external_links, newLink] })); setNewLink({ label: "", url: "", type: "stats" }); } };
  const copyToClipboard = (text, key) => { navigator.clipboard.writeText(text); setCopied({ [key]: true }); setTimeout(() => setCopied({}), 2000); };

  return {
    broadcasts, events, teams, showForm, setShowForm, showPastGameForm, setShowPastGameForm,
    showImportModal, setShowImportModal, thumbnailInputRef, editingId, setEditingId,
    formData, setFormData, saving, copied, newLink, setNewLink,
    expandedBroadcast, vodForm, setVodForm, savingVod, syncingScores,
    extractingColor, uploadingThumbnail, archivingBroadcast, generatingThumbnail,
    savingPastGame, pastGameData, setPastGameData,
    selectedIds, setSelectedIds, bulkEditField, setBulkEditField, bulkEditValue, setBulkEditValue,
    fetchBroadcasts, handleExtractTeamColor, handleGenerateThumbnail, handleThumbnailUpload,
    selectTeam, setTeamTBD, handleCreate, handleEdit, handleGoLive, handleEndLive,
    handleDelete, handleMarkComplete, handleToggleHidden, handleSyncScores, handleSyncAllScores,
    handleArchiveToOnDemand, toggleExpanded, handleSaveVod, handleSavePastGame,
    toggleSelectAll, toggleSelect, handleBulkDelete, handleBulkEdit, addLink, copyToClipboard
  };
}
